import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@mh-wms/api-client';
import { authedFetch } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';

export const TABLE_THEME_PRESETS = ['modern', 'compact', 'banded', 'grid'] as const;
export type TableThemePreset = (typeof TABLE_THEME_PRESETS)[number];

export const TABLE_THEME_OPTIONS: ReadonlyArray<{
  value: TableThemePreset;
  label: string;
  description: string;
}> = [
  { value: 'modern', label: 'Modern', description: 'Balanced spacing and a clean highlighted header.' },
  { value: 'compact', label: 'Compact', description: 'Fits more warehouse records on each screen.' },
  { value: 'banded', label: 'Banded rows', description: 'Alternating rows make long reports easier to scan.' },
  { value: 'grid', label: 'Full grid', description: 'Clear vertical and horizontal boundaries for every value.' },
];

interface TableThemeValue {
  preset: TableThemePreset;
  isLoading: boolean;
  isSaving: boolean;
  savePreset: (preset: TableThemePreset) => Promise<TableThemePreset>;
}

const DEFAULT_VALUE: TableThemeValue = {
  preset: 'modern',
  isLoading: false,
  isSaving: false,
  savePreset: async () => 'modern',
};

const TableThemeContext = createContext<TableThemeValue>(DEFAULT_VALUE);
const QUERY_KEY = ['system-settings', 'table-theme'] as const;

function isPreset(value: unknown): value is TableThemePreset {
  return TABLE_THEME_PRESETS.includes(value as TableThemePreset);
}

async function loadTableTheme(): Promise<TableThemePreset> {
  const response = await authedFetch('/api/settings/table-theme');
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
  const result = (await response.json()) as { preset?: unknown };
  return isPreset(result.preset) ? result.preset : 'modern';
}

async function updateTableTheme(preset: TableThemePreset): Promise<TableThemePreset> {
  const response = await authedFetch('/api/settings/table-theme', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preset }),
  });
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
  return preset;
}

export function TableThemeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: loadTableTheme,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  const mutation = useMutation({
    mutationFn: updateTableTheme,
    onSuccess: (preset) => {
      queryClient.setQueryData(QUERY_KEY, preset);
      toast.success('Table theme updated for everyone.');
    },
    onError: () => toast.error('Table theme could not be saved.'),
  });
  const value = useMemo<TableThemeValue>(
    () => ({
      preset: query.data ?? 'modern',
      isLoading: query.isLoading,
      isSaving: mutation.isPending,
      savePreset: mutation.mutateAsync,
    }),
    [mutation.isPending, mutation.mutateAsync, query.data, query.isLoading],
  );

  return <TableThemeContext.Provider value={value}>{children}</TableThemeContext.Provider>;
}

export function useTableTheme(): TableThemeValue {
  return useContext(TableThemeContext);
}
